import { Fragment } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset, renderIcon } from '@opallabs/kuiper'
import { AccountName } from '@opallabs/periodic/src/AccountName'
import { OnShowAssetPickerProps } from '@opallabs/periodic/src/Content/helpers/types'
import { ContentTextInput } from '@opallabs/periodic/src/ContentTextInput'
import { SingleAssetPicker } from '@opallabs/periodic/src/SingleAssetPicker'
import { sharedColors } from '../sharedStyles/styles'
import { styles } from './styles'

export interface Props {
  accountName: string
  accountUsername: string
  bio: string
  coverImage: AssetReference | null
  display: string
  displayName: string
  isPresenting: boolean
  location: string
  onDragDrop: (
    e: React.DragEvent<HTMLDivElement>,
    args: OnShowAssetPickerProps
  ) => void
  onExpandAsset: (assetId: number) => void
  onInputChange: (val: string, dest: string) => void
  onRemoveAsset: (assetId: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  postType: string
  profileImage: AssetReference | null
  readOnly: boolean
  truncate: boolean
  url: string
}

export const MobileStandard: React.FC<Props> = ({
  accountName,
  accountUsername,
  bio,
  coverImage,
  isPresenting,
  location,
  onDragDrop,
  onExpandAsset,
  onInputChange,
  onRemoveAsset,
  onShowAssetPicker,
  profileImage,
  readOnly,
  truncate,
  url,
}) => (
  <Fragment>
    {coverImage ? (
      <Asset
        hasAssetOverlay={!readOnly || isPresenting}
        assetReference={coverImage}
        overrideStyles={{
          asset: styles.coverAsset,
          assetBase: styles.coverAsset,
        }}
        onEmbiggen={() => onExpandAsset(coverImage.id || 0)}
        onRemove={() => onRemoveAsset(coverImage.id || 0)}
      />
    ) : (
      <SingleAssetPicker
        allowedUploadTypes="image"
        onDragDrop={(e) => onDragDrop(e, { destination: 'cover_image' })}
        onShowAssetPicker={() =>
          onShowAssetPicker({ destination: 'cover_image' })
        }
        readOnly={readOnly}
        style={styles.coverAsset}
      />
    )}
    <div css={styles.profile}>
      {profileImage && profileImage.id ? (
        <Asset
          assetReference={profileImage}
          hasAssetOverlay={!readOnly || isPresenting}
          removeable={false}
          downloadable={false}
          overrideStyles={{
            asset: styles.profileAsset,
            assetBase: styles.profileAssetBase,
          }}
          onEmbiggen={() => onExpandAsset(profileImage.id || 0)}
          onRemove={() => onRemoveAsset(profileImage.id || 0)}
        />
      ) : (
        <SingleAssetPicker
          allowedUploadTypes="image"
          borderRadius="100%"
          onDragDrop={(e) => onDragDrop(e, { destination: 'profile_image' })}
          onShowAssetPicker={() =>
            onShowAssetPicker({ destination: 'profile_image' })
          }
          readOnly={readOnly}
          showButtonGroup={false}
          style={styles.profileAsset}
        />
      )}
      <div css={styles.actionButtonGroup}>
        <div css={styles.whiteBtn}>
          {renderIcon('twitter-mobile-stream-message', {
            height: 12,
            width: 14,
            ariaMode: 'hidden',
          })}
        </div>
        <div css={styles.blackBtn}>Follow</div>
      </div>
      <div>
        <AccountName name={accountName} style={styles.accountName} size={22} />
        <AccountName
          name={accountUsername}
          style={styles.accountUsername}
          size={16}
          accountHandle="@"
          color={sharedColors.medGray}
        />
        <ContentTextInput
          value={bio || ''}
          placeholder="Add bio..."
          style={styles.getBio}
          inputStyle={styles.socialLinkStyle}
          characterLimit={160}
          characterCounterOptions={{ zIndex: 9999 }}
          highlightSocialTags={true}
          onChange={(val: string) => onInputChange(val, 'bio')}
        />
      </div>
      <div css={styles.footerWrapper}>
        <div css={styles.footerItem}>
          {renderIcon('twitter-mobile-profile-location', {
            height: 14,
            width: 12,
            ariaMode: 'hidden',
          })}
          <ContentTextInput
            value={location || ''}
            placeholder="Add location..."
            style={styles.getFooterInput('location')}
            inputStyle={styles.footerInputStyle}
            maxRows={1}
            characterLimit={30}
            characterCounterOptions={{ zIndex: 9999 }}
            highlightSocialTags={true}
            onChange={(val: string) => onInputChange(val, 'location')}
          />
        </div>
        <div css={styles.footerItem}>
          {renderIcon('twitter-mobile-profile-url', {
            height: 14,
            width: 14,
            ariaMode: 'hidden',
          })}
          <ContentTextInput
            value={url || ''}
            placeholder="Add website URL..."
            style={styles.getFooterInput('url')}
            inputStyle={styles.footerInputStyle}
            maxRows={2}
            characterLimit={100}
            characterCounterOptions={{ zIndex: 9999 }}
            truncate={truncate}
            truncationLimit={40}
            highlightSocialTags={true}
            onChange={(val: string) => onInputChange(val, 'url')}
          />
        </div>
        <div css={styles.footerItem}>
          {renderIcon('twitter-mobile-profile-calendar', {
            height: 14,
            width: 14,
            ariaMode: 'hidden',
          })}
          <span css={styles.joinedWrapper}>Joined MM YYYY</span>
        </div>
      </div>
    </div>
  </Fragment>
)
